<template>
  <div class="cluster-supplier-selector">
    <!-- Industry Filter -->
    <v-row class="mb-3" dense>
      <v-col cols="12" class="pa-0 ma-0 d-flex align-start">
        <v-combobox
          v-model="selectedIndustries"
          :items="industry_options_formatted"
          item-text="text"
          item-value="value"
          return-object
          :loading="loadingIndustries"
          x-small-chips
          multiple
          chips
          deletable-chips
          :rounded="false"
          :solo="true"
          clearable
          dense
          outlined
          label="Industry"
          x-small
          :menu-props="{ maxWidth: '400', maxHeight: '400' }"
          class="pr-2 industry-combobox flex-grow-1"
          style="min-height: 40px;"
          @click:clear="clearIndustryFilter()"
          @change="applyFilters"
        >
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title :style="{ paddingLeft: (item.level - 1) * 20 + 'px' }">
                {{ item.text }}
              </v-list-item-title>
            </v-list-item-content>
          </template>
          <template v-slot:selection="{ attrs, item, select, selected }">
            <v-chip
              color="primary"
              label
              small
              v-bind="attrs"
              :input-value="selected"
              close
              @click="select"
              @click:close="removeIndustry(item)"
              class="mr-1"
            >
              {{ item.text }}
            </v-chip>
          </template>
        </v-combobox>
      </v-col>
    </v-row>
    
    <!-- Search Bar -->
    <v-combobox
      v-model="term_chips"
      :search-input.sync="keyword_search"
      small-chips
      clearable
      outlined
      dense
      label="Enter supplier names using separated keywords"
      multiple
      small
      ref="keywords"
      append-outer-icon="mdi-magnify"
      class="mb-4"
      @click:append-outer="performSearch"
      @click:clear="clearSearch"
      @keydown.enter.prevent="handleKeywordEnter"
      @keydown.tab.prevent="handleKeywordTab"
    >
      <template v-slot:selection="{ attrs, item, select, selected }">
        <v-chip
          color="primary"
          small
          label
          v-bind="attrs"
          :input-value="selected"
          close
          @click="select"
          @click:close="removeKeyword(item)"
        >{{ item }}</v-chip>
      </template>
    </v-combobox>
    
    <!-- Select All Button -->
    <div v-if="suppliers.length > 0" class="mb-3">
      <v-btn
        small
        outlined
        @click="toggleSelectAll"
        :disabled="loading"
      >
        <v-icon left>{{ allSelected ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}</v-icon>
        {{ allSelected ? 'Deselect All' : 'Select All' }}
      </v-btn>
      <span class="ml-2 caption grey--text">
        {{ suppliers.length.toLocaleString() }}{{ totalResults > 0 ? ` of ${totalResults.toLocaleString()}` : '' }} suppliers
      </span>
    </div>
    
    <!-- Loading State -->
    <v-progress-linear v-if="loading" indeterminate class="mb-4" />
    
      <!-- Supplier List -->
      <div v-if="!loading" class="supplier-list" style="max-height: 400px; overflow-y: auto;" @scroll="onScroll">
      <v-list dense>
        <v-list-item
          v-for="supplier in displayedSuppliers"
          :key="supplier.id"
          @click="toggleSelection(supplier)"
          :class="{ 'selected': isSelected(supplier.id) }"
          class="supplier-item"
        >
          <v-list-item-content>
            <v-list-item-title>{{ supplier.name }}</v-list-item-title>
            <v-list-item-subtitle v-if="supplier.abn">
              ABN: {{ supplier.abn }}
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action>
            <v-checkbox
              :input-value="isSelected(supplier.id)"
              color="primary"
              @click.stop="toggleSelection(supplier)"
              hide-details
              class="ma-0 pa-0"
            />
          </v-list-item-action>
        </v-list-item>
      </v-list>
      
      <!-- Load More Button -->
      <div v-if="hasMoreResults && !loadingMore" class="text-center pa-2">
        <v-btn small outlined @click="loadMoreResults">
          Load More
        </v-btn>
      </div>
      
      <!-- Loading More Indicator -->
      <div v-if="loadingMore" class="text-center pa-2">
        <v-progress-circular size="20" indeterminate></v-progress-circular>
      </div>
      
      <!-- No results message -->
      <div v-if="displayedSuppliers.length === 0 && !loading" class="text-center pa-4">
        <v-icon size="48" color="grey lighten-1">mdi-magnify</v-icon>
        <p class="grey--text mt-2">No suppliers found</p>
        <p class="caption grey--text">Try adjusting your search terms</p>
      </div>
    </div>
    
    <!-- Selected count and Add Button -->
    <div v-if="selectedSuppliers.length > 0" class="mt-4">
      <v-alert type="info" dense class="mb-3">
        {{ selectedSuppliers.length }} supplier{{ selectedSuppliers.length > 1 ? 's' : '' }} selected
      </v-alert>
      
      <v-btn
        color="primary"
        block
        :loading="adding"
        @click="addToCluster"
      >
        Add {{ selectedSuppliers.length }} to Cluster
      </v-btn>
    </div>
  </div>
</template>

<script>
import { searchSuppliers, updateClusters, fetchIndustries } from "./APIManager.js";

export default {
  name: "ClusterSupplierSelector",
  props: {
    clusterId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      term_chips: [],
      keyword_search: '',
      suppliers: [],
      selectedSuppliers: [],
      loading: false,
      adding: false,
      currentPage: 1,
      pageSize: 300,
      hasMoreResults: false,
      loadingMore: false,
      totalResults: 0,
      selectedIndustries: [],
      industry_options: [],
      industry_options_formatted: [],
      loadingIndustries: false
    };
  },
  computed: {
    displayedSuppliers() {
      return this.suppliers;
    },
    
    allSelected() {
      return this.suppliers.length > 0 && this.selectedSuppliers.length === this.suppliers.length;
    }
  },
  mounted() {
    this.loadFilterOptions();
    this.loadSuppliers();
  },
  methods: {
    async loadFilterOptions() {
      // Load industries
      this.loadingIndustries = true;
      try {
        const data = await fetchIndustries();
        if (data && Array.isArray(data)) {
          this.industry_options = data;
          this.formatIndustriesForCombobox(data);
        }
      } catch (error) {
        console.error('Error loading industries:', error);
      } finally {
        this.loadingIndustries = false;
      }
    },
    
    formatIndustriesForCombobox(industries, level = 1) {
      // Recursively flatten nested industry structure with level information
      const flatten = (items, currentLevel = 1) => {
        let result = [];
        items.forEach(item => {
          result.push({
            text: item.name,
            value: item.id,
            level: currentLevel,
            parent_id: item.parent_id || null
          });
          if (item.children && item.children.length > 0) {
            result = result.concat(flatten(item.children, currentLevel + 1));
          }
        });
        return result;
      };
      this.industry_options_formatted = flatten(industries);
    },
    
    async loadSuppliers() {
      this.loading = true;
      this.currentPage = 1;
      this.suppliers = [];
      this.selectedSuppliers = [];
      
      try {
        // Use searchSuppliers for initial load - join chips with commas for multiple terms
        const searchText = this.term_chips.length > 0 ? this.term_chips.join(',') : '';
        
        // Build filters object
        const filters = {};
        if (this.selectedIndustries && this.selectedIndustries.length > 0) {
          // Extract industry IDs from selected items
          filters.industry_id = this.selectedIndustries.map(i => i.value);
        }
        
        const response = await searchSuppliers(searchText, this.currentPage, this.pageSize, filters);
        
        this.suppliers = response.data || [];
        this.totalResults = response.total || 0;
        this.hasMoreResults = response.has_more || false;
        
      } catch (error) {
        console.error('Error loading suppliers:', error);
        this.$emit('error', 'Failed to load suppliers');
      } finally {
        this.loading = false;
      }
    },
    
    performSearch() {
      this.loadSuppliers();
    },
    
    clearSearch() {
      this.term_chips = [];
      this.loadSuppliers();
    },
    
    removeKeyword(item) {
      this.term_chips.splice(this.term_chips.indexOf(item), 1);
      this.term_chips = [...this.term_chips];
      if (this.term_chips.length === 0) {
        this.clearSearch();
      } else {
        this.performSearch();
      }
    },
    
    async loadMoreResults() {
      if (this.loadingMore || !this.hasMoreResults) return;
      
      this.loadingMore = true;
      this.currentPage++;
      
      try {
        const searchText = this.term_chips.length > 0 ? this.term_chips.join(',') : '';
        
        // Build filters object
        const filters = {};
        if (this.selectedIndustries && this.selectedIndustries.length > 0) {
          // Extract industry IDs from selected items
          filters.industry_id = this.selectedIndustries.map(i => i.value);
        }
        
        const response = await searchSuppliers(searchText, this.currentPage, this.pageSize, filters);
        
        const newSuppliers = response.data || [];
        const wasAllSelected = this.selectedSuppliers.length === this.suppliers.length;
        this.suppliers = [...this.suppliers, ...newSuppliers];
        this.hasMoreResults = response.has_more || false;
        if (wasAllSelected && newSuppliers.length > 0) {
          this.selectedSuppliers = [...this.selectedSuppliers, ...newSuppliers];
        }
        
      } catch (error) {
        console.error('Error loading more suppliers:', error);
        this.$emit('error', 'Failed to load more suppliers');
        this.currentPage--; // Revert page on error
      } finally {
        this.loadingMore = false;
      }
    },
    
    onScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      const threshold = 50;
      
      if (scrollHeight - scrollTop - clientHeight < threshold && this.hasMoreResults && !this.loadingMore) {
        this.loadMoreResults();
      }
    },
    
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedSuppliers = [];
      } else {
        this.selectedSuppliers = [...this.suppliers];
      }
    },
    
    toggleSelection(supplier) {
      const index = this.selectedSuppliers.findIndex(s => s.id === supplier.id);
      if (index > -1) {
        this.selectedSuppliers.splice(index, 1);
      } else {
        this.selectedSuppliers.push(supplier);
      }
    },
    
    isSelected(supplierId) {
      return this.selectedSuppliers.some(s => s.id === supplierId);
    },
    
    removeIndustry(item) {
      const index = this.selectedIndustries.findIndex(i => i.value === item.value);
      if (index > -1) {
        this.selectedIndustries.splice(index, 1);
        this.applyFilters();
      }
    },
    
    clearIndustryFilter() {
      this.selectedIndustries = [];
      this.applyFilters();
    },
    
    applyFilters() {
      // Reload suppliers with new filters
      this.loadSuppliers();
    },
    
    handleKeywordEnter() {
      // Add the current search input as a chip if it has a value
      if (this.keyword_search && this.keyword_search.trim()) {
        const trimmedValue = this.keyword_search.trim();
        if (!this.term_chips.includes(trimmedValue)) {
          this.term_chips.push(trimmedValue);
        }
        this.keyword_search = '';
      }
      // Trigger search after adding the chip
      this.$nextTick(() => {
        this.performSearch();
      });
    },
    
    handleKeywordTab() {
      // Add the current search input as a chip if it has a value
      if (this.keyword_search && this.keyword_search.trim()) {
        const trimmedValue = this.keyword_search.trim();
        if (!this.term_chips.includes(trimmedValue)) {
          this.term_chips.push(trimmedValue);
        }
        this.keyword_search = '';
      }
      // Trigger search after adding the chip
      this.$nextTick(() => {
        this.performSearch();
      });
    },
    
    async addToCluster() {
      if (this.selectedSuppliers.length === 0) return;
      
      this.adding = true;
      try {
        const supplierIds = this.selectedSuppliers.map(s => s.id);
        await updateClusters([this.clusterId], 'supplier', supplierIds);
        
        this.$emit('entities-added', this.selectedSuppliers);
        this.$emit('success', `Added ${this.selectedSuppliers.length} supplier${this.selectedSuppliers.length > 1 ? 's' : ''} to cluster`);
        
        // Clear selection
        this.selectedSuppliers = [];
        
      } catch (error) {
        console.error('Error adding suppliers to cluster:', error);
        this.$emit('error', 'Failed to add suppliers to cluster');
      } finally {
        this.adding = false;
      }
    }
  }
};
</script>

<style scoped>
.supplier-item {
  cursor: pointer;
  transition: background-color 0.2s;
}

.supplier-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.supplier-item.selected {
  background-color: rgba(25, 118, 210, 0.08);
}

.supplier-list {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.industry-combobox >>> .v-input__control {
  min-height: 40px;
}

.industry-combobox >>> .v-input__slot {
  min-height: 40px;
  align-items: flex-start;
  padding-top: 4px;
}
</style>
